import DemoProject from "../../assets/svg/DemoProject.svg";
import CreateProject from "../../assets/svg/CreateProjectIcon.svg";
import CreateProjectForm from "@/components/project/CreateProjectForm";
import { useEffect, useMemo, useState } from "react";
import { useUser } from "@/hooks/useUser";
import useCreateDemoProjectQuary from "@/api/query/useCreateDemoProjectQuary";
import { useNavigate } from "react-router-dom";
import useProjectQuery from "@/api/query/useProjectQuery";
import useProjectWithoutCalulationQuery from "@/api/query/useProjectWithoutCalulationQuery";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";
import NoTaskImage from "../../assets/png/NoSearchTask.png";
import { useTranslation } from "react-i18next";
import Loader from "../common/Loader";
import Spinner from "../ui/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/api/query/querykeys";

function NoProject() {
  const [open, setOpen] = useState<boolean>(false);
  const close = () => {
    setOpen(false);
  };
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const projectQuery = useProjectQuery();
  const projectWithoutCalQuery = useProjectWithoutCalulationQuery(false);

  const currentOrg = useMemo(() => {
    const activeOrgId = localStorage.getItem("organisation-id");
    if (!user?.userOrganisation?.length) return undefined;
    return (
      user.userOrganisation.find((org) => org.organisationId === activeOrgId) ??
      user.userOrganisation[0]
    );
  }, [user]);

  const organisationId = currentOrg?.organisationId ?? localStorage.getItem("organisation-id") ?? "";

  const CreateDemoProject = useCreateDemoProjectQuary(organisationId);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!projectWithoutCalQuery.isFetched) {
      projectWithoutCalQuery.refetch();
    }
  }, [projectWithoutCalQuery.isFetched]);

  const createDemoProjectSubmit = () => {
    setIsLoading(true);
    CreateDemoProject.refetch()
      .then(async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.portfolioDashboard] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjects] }),
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getProjectsNocal] }),
          projectQuery.refetch(),
          projectWithoutCalQuery.refetch(),
        ]);
      })
      .then(() => {
        navigate("/projects");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isAdmin = useMemo(() => {
    return user?.userOrganisation?.some(
      (org) => org.role === UserRoleEnumValue.ADMINISTRATOR
    ) ?? false;
  }, [user?.userOrganisation]);

  const canCreateProject = useMemo(() => {
    return (
      isAdmin ||
      (user?.managedPortfolioIds && user.managedPortfolioIds.length > 0) ||
      (user?.managedProgramIds && user.managedProgramIds.length > 0)
    );
  }, [isAdmin, user?.managedPortfolioIds, user?.managedProgramIds]);

  return (
    <>
      <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-slate-50/50 overflow-y-auto">
        {projectQuery.isRefetching && (
          <Loader className="absolute top-0 left-0 h-full w-full z-50" />
        )}

        {!canCreateProject && (
          <div className="flex flex-col items-center justify-center gap-6 max-w-md text-center py-12">
            <div className="p-4 bg-white rounded-3xl shadow-sm border border-slate-100">
              <img src={NoTaskImage} className="w-72 h-auto" alt="No Projects" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-slate-800">
                {t("don't_have_project")}
              </h2>
              <p className="text-sm font-medium text-slate-500">
                {t("ask_admin_to_assign")}
              </p>
            </div>
          </div>
        )}

        {canCreateProject && (
          <div className="flex flex-col items-center justify-center gap-8 w-full max-w-3xl py-8">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-xs font-semibold uppercase tracking-wider mb-1">
                <span>✨</span> {t("get_started")}
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                {t("create_first_project")}
              </h1>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {t("choose_workspace_setup_description")}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full mt-2">
              <div className="relative group w-full sm:w-80">
                <div
                  className="bg-gradient-to-br from-[#535EF9] to-[#3B82F6] text-white shadow-xl shadow-blue-500/20 hover:shadow-2xl hover:shadow-blue-500/30 hover:scale-[1.02] transition-all duration-300 rounded-2xl p-6 border border-blue-400/30 flex flex-col items-center text-center justify-between min-h-[280px] cursor-pointer overflow-hidden"
                  onClick={createDemoProjectSubmit}
                >
                  <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md text-white font-bold text-[10px] px-2.5 py-0.5 rounded-full uppercase tracking-wider border border-white/20">
                    {t("recommended_option")}
                  </div>

                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mt-4 group-hover:scale-110 transition-transform duration-300 shadow-inner">
                    <img
                      src={DemoProject}
                      alt="Demo"
                      className="h-9 w-9 invert"
                    />
                  </div>

                  <div className="space-y-1.5 my-3">
                    <h3 className="text-xl font-bold text-white">
                      {t("use_a_demo_project")}
                    </h3>
                    <p className="text-xs text-blue-100 leading-relaxed px-2">
                      {t("use_a_demo_project_description")}
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-2 text-xs font-bold bg-white text-[#535EF9] hover:bg-slate-50 shadow-md px-5 py-2.5 rounded-xl transition-all mt-2 group-hover:shadow-lg">
                    <span>{t("generate_demo_projects")}</span>
                    <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                  </div>
                </div>

                {isLoading && (
                  <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center gap-3 z-20 text-white p-4">
                    <Spinner color="#ffffff" className="h-9 w-9" />
                    <span className="text-xs font-medium text-center">
                      {t("creating_demo_projects")}
                    </span>
                  </div>
                )}
              </div>

              <div className="w-full sm:w-80">
                <div
                  onClick={() => (!isLoading ? setOpen(true) : undefined)}
                  className={`bg-white text-slate-800 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 rounded-2xl p-6 border border-slate-200/80 flex flex-col items-center text-center justify-between min-h-[280px] cursor-pointer group ${
                    isLoading ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mt-4 group-hover:scale-110 transition-transform duration-300 border border-blue-100">
                    <img
                      src={CreateProject}
                      alt="Create"
                      className="h-9 w-9"
                    />
                  </div>

                  <div className="space-y-1.5 my-3">
                    <h3 className="text-xl font-bold text-slate-900">
                      {t("use_a_create_project")}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed px-2">
                      {t("use_a_create_project_description")}
                    </p>
                  </div>

                  <div className="inline-flex items-center gap-2 text-xs font-bold text-white bg-gradient-to-r from-[#535EF9] to-[#3B82F6] hover:from-[#4650DF] hover:to-[#3172E9] shadow-md px-5 py-2.5 rounded-xl transition-all mt-2 group-hover:shadow-lg">
                    <span>{t("create_blank_project")}</span>
                    <span className="text-sm font-semibold">+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {open && <CreateProjectForm handleClosePopUp={close} />}
    </>
  );
}
export default NoProject;
